import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ParameterTypeToTSType} from 'insights/parameters/data/ValueValidators';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL as AnalyticsParameterConfig} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AnalyticsParameterFieldError from './AnalyticsParameterFieldError';
import AnalyticsParameterValueEditor, {InputProps} from './AnalyticsParameterValueEditor';
import getParameterTypeAndConfigSetting from './data/getParameterTypeAndConfigSetting';
import getParameterValueObject from './data/getParameterValueObject';
import sortParameterConfigs from './data/sortParameterConfigs';
import {VALUE_VALIDATORS} from './data/ValueValidators';

export interface ParameterValuesState {
  [configId: string]: JSONValue;
}

export interface ParameterValueFieldListProps {
  configs: Array<AnalyticsParameterConfig>;
  selectedValues: ParameterValuesState;
  onSelectValue: (configId: string, valueJson: JSONValue) => void;
  disabled?: boolean;
}

export default function ParameterValueFieldList({
  configs,
  selectedValues,
  onSelectValue,
  disabled,
}: ParameterValueFieldListProps): JSX.Element {
  const sortedConfigs = sortParameterConfigs(configs);

  return (
    <>
      {sortedConfigs.map((config, index) => {
        const typeAndConfigSetting = getParameterTypeAndConfigSetting(config);
        const {type, configSetting} = typeAndConfigSetting;
        const value = getParameterValueObject(config, selectedValues[config.id] ?? null);
        const isValid = VALUE_VALIDATORS[type](value.valueJson);

        const inputProps: InputProps<typeof type> = {
          valueJson: value.valueJson as ParameterTypeToTSType[typeof type],
          paramConfigSetting: configSetting,
          onChange: (valueJson) => {
            onSelectValue(config.id, valueJson);
          },
          allowAutoFocus: index === 0,
          disabled,
        };

        return (
          <FormGroup key={config.id} className={css(styles.inputWrapper)}>
            <FieldLabel label={config.systemName} />
            <AnalyticsParameterValueEditor paramType={type} inputProps={inputProps} />
            {!isValid && <AnalyticsParameterFieldError type={type} />}
          </FormGroup>
        );
      })}
    </>
  );
}

const styles = StyleSheet.create({
  inputWrapper: {
    marginBottom: 12,
    ':last-child': {
      marginBottom: 0,
    },
  },
});
